import React, { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { Routes } from 'common/enums';
import { AppState, AppTranslationHelper } from 'common/types';
import { setDocumentTitle } from 'common/utils';
import { LoadScreen } from 'common/components';
import styles from './userList.module.css';
import { appContext } from 'common/contexts';
import { deleteUser, toggleUserStatus } from 'services/userApi';
import { toast } from 'react-toastify';
import { EditSquareOutlineTwoTone, TrashCanOutlineTwoTone } from 'common/icons';
import { useTranslation } from 'react-i18next';

type UserListProps = {
  context: AppState;
  translation: AppTranslationHelper;
};

interface User {
  id: string;
  email: string;
  displayName?: string;
  role?: string;
  createdAt?: any;
  parentId?: string;
  enabled?: boolean;
}

const UserList: React.FC<UserListProps> = ({ context, translation }) => {
  const { t } = translation;
  const { t: t2 } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [toggleLoading, setToggleLoading] = useState<string | null>(null);
  const { users } = context;

  useEffect(() => {
    const title = [t('admin.title'), t2('admin.users.management', 'Gestión de Usuarios')];
    setDocumentTitle(title);
  }, [t, t2]);

  const handleDeleteUser = async (userId: string) => {
    // Find the user to check if it's an admin
    const user = users.find(u => u.id === userId);
    if (user?.role === 'admin') {
      toast.error(t2('admin.users.cannotDeleteAdmin', 'No se puede eliminar el usuario administrador'));
      return;
    }
    
    if (!window.confirm(t2('admin.users.confirmDelete', '¿Estás seguro de que deseas eliminar este usuario?'))) {
      return;
    }
    try {
      setDeleteLoading(userId);
      await deleteUser(userId);
      toast.success(t2('admin.users.userDeleted', 'Usuario eliminado exitosamente'));
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      toast.error(t2('admin.users.errorDeletingUser', 'Error al eliminar usuario'));
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: boolean) => {
    try {
      setToggleLoading(userId);
      await toggleUserStatus(userId, !currentStatus);
      toast.success(t2('admin.users.userStatusChanged', 'Usuario {{status}} exitosamente', { status: !currentStatus ? t2('admin.users.enabled', 'habilitado') : t2('admin.users.disabled', 'deshabilitado') }));
    } catch (error) {
      console.error('Error al cambiar estado del usuario:', error);
      toast.error(t2('admin.users.errorChangingStatus', 'Error al cambiar estado del usuario'));
    } finally {
      setToggleLoading(null);
    }
  };

  const sortedUsers = [...users].sort((a, b) => {
    // Admin user always at the top
    if (a.role === 'admin') return -1;
    if (b.role === 'admin') return 1;
    return 0;
  });

  if (loading) {
    return <LoadScreen />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{t2('admin.users.management', 'Gestión de Usuarios')}</div>
        <div className={styles.actions}>
          <Link className="btn btn-primary" to={Routes.AdminUserCreate}>
            <span>{t2('admin.users.createUser', 'Crear Usuario')}</span>
          </Link>
        </div>
      </div>

      <div className={styles.content}>
        {users.length > 0 ? (
          <table className="table table-hover">
            <thead>
              <tr>
                <th>{t2('admin.users.tableHeaders.email')}</th>
                <th>{t2('admin.users.tableHeaders.name')}</th>
                <th>{t2('admin.users.tableHeaders.role')}</th>
                <th>{t2('admin.users.tableHeaders.status')}</th>
                <th>{t2('admin.users.tableHeaders.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((user) => {
                const isAdmin = user.role === 'admin';
                console.log(user)
                return (
                  <tr key={user.id} className={isAdmin ? styles.adminRow : ''}>
                    <td>{user.email}</td>
                    <td>{user.displayName || '-'}</td>
                    <td>
                      <span className={`badge ${isAdmin ? 'bg-danger' : 'bg-primary'}`}>
                        {user.role === 'admin' ? 'Admin' : 
                         user.role === 'user' ? t2('admin.users.roleUser', 'Usuario') : 
                         user.role === 'cashier' ? t2('admin.users.roleCashier', 'Cajero') : 
                         user.role || t2('admin.users.roleUser', 'Usuario')}
                      </span>
                    </td>
                    <td>
                      <div className="form-check form-switch d-flex align-items-center">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id={`status-switch-${user.id}`}
                          checked={isAdmin || user.disabled === false}
                          onChange={() => !isAdmin && handleToggleUserStatus(user.id, user.disabled !== false)}
                          disabled={isAdmin || toggleLoading === user.id}
                        />
                        {toggleLoading === user.id ? (
                          <span style={{marginLeft: '1rem'}} className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        ) : (
                          <label style={{ marginLeft: '1rem' }} className="form-check-label" htmlFor={`status-switch-${user.id}`}>
                            {isAdmin ? t2('admin.users.alwaysEnabled', 'Siempre habilitado') : (user.disabled === false ? t2('admin.users.enabled', 'Habilitado') : t2('admin.users.disabled', 'Deshabilitado'))}
                          </label>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex">
                        <Link
                          to={Routes.AdminUserEdit.replace(':id', user.id)}
                          className={styles.iconButton}
                          title={t2('admin.users.editUser', 'Editar usuario')}
                        >
                          <EditSquareOutlineTwoTone />
                        </Link>
                        <button
                          className={styles.iconButton}
                          onClick={() => handleDeleteUser(user.id)}
                          disabled={isAdmin || deleteLoading === user.id}
                          style={{ display: isAdmin ? 'none' : 'block' }}
                          title={t2('admin.users.deleteUser', 'Eliminar usuario')}
                        >
                          {deleteLoading === user.id ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          ) : <TrashCanOutlineTwoTone />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className={styles.empty}>
            <p>{t2('admin.users.noUsers', 'No hay usuarios registrados')}</p>
            <p>{t2('admin.users.createFirstUser', 'Crea un nuevo usuario para comenzar')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserList;
